import { selectRevisionEntityById } from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import {
  CaptureTreeEntity,
  CaptureTreeEntityType,
  CaptureTreePointCloudType,
  RegistrationStatus,
  RevisionScanEntity,
  type CaptureTreeEntityRevision,
} from "@faro-lotv/service-wires";
import { Vector3Tuple } from "three";
import { selectRevisionEntityWorldTransformCache } from "../store/data-preparation-ui/data-preparation-ui-selectors";
import { RegistrationStatusMap } from "../store/registration-status-selectors";

/** The size factor when the element is being hovered. */
export const HOVER_FACTOR = 1.2;

/** Render order for elements in the canvas */
export enum RenderOrder {
  /** scan point clouds */
  scans = 0,
  /** highlighted scan point clouds */
  highlightedScans = 1,
  /** PartOf connections between scans */
  partOfConnections = 2,
  /** All other connections between scans */
  connections = 3,
  /** markers representing the scan point cloud */
  placeholders = 4,
  /** pin marker used to transform the point clouds */
  pin = 5,
}

/**
 * @param entity The entity to check
 * @param statusMap A map from entity IDs to their registration statuses
 * @returns `true` if the given entity should be reviewed by the user
 */
export function doesEntityRequireReview(
  entity: CaptureTreeEntity,
  statusMap: RegistrationStatusMap,
): boolean {
  return (
    // Not including RegistrationStatus.unknown here, to support old projects that were not touched again
    statusMap[entity.id] === RegistrationStatus.unregistered ||
    statusMap[entity.id] === RegistrationStatus.unverified
  );
}

/**
 * @param id The ID of the iElement or capture tree entity to get the name of.
 * @returns The name of the IElement, or the name of the capture tree entity, or the ID if the name or element is missing.
 */
export function useElementName(id: GUID): string {
  const iElement = useAppSelector(selectIElement(id));
  const revisionEntityName = useAppSelector(selectRevisionEntityById(id))?.name;
  return revisionEntityName ?? iElement?.name ?? id;
}

/**
 * @param scanEntity The scan entity to get the position for.
 * @returns The position where the scanner was located during the scan.
 */
export function useScanPosition(scanEntity?: RevisionScanEntity): Vector3Tuple {
  const revisionTransform = useAppSelector(
    selectRevisionEntityWorldTransformCache(scanEntity?.id),
  );
  return revisionTransform.position;
}

/**
 * @param scanEntity The scan entity to check.
 * @returns Whether we can display the scan entity in the 3D view.
 */
export function isScanEntityUsable(scanEntity: RevisionScanEntity): boolean {
  return !!scanEntity.pointClouds?.some(
    // A PoTree point cloud is needed for rendering
    (pc) => pc.type === CaptureTreePointCloudType.poTree,
  );
}

/**
 * @param entity The revision entity to check.
 * @returns true, if the entity is of type cluster, else false.
 */
export function isEntityCluster(entity: CaptureTreeEntityRevision): boolean {
  return entity.type === CaptureTreeEntityType.cluster;
}

/**
 * @param scanEntity The scan entity to check.
 * @returns Whether the scan entity is fixed.
 */
export function isScanEntityFixed(scanEntity: RevisionScanEntity): boolean {
  return scanEntity.isFixed === true;
}
